// =============================================================================
// R-239 P4 Task 9 — Profile Field Learning panel.
// Displays per-field OCR learning lifecycle, confidence, skip-rate, and signal
// health for a selected job type. Pinned-first sort: demoted + drifting rows
// appear at the top (highest admin attention needed). Sortable by confidence
// and skip-rate via column header click. No sparklines (Task 11).
// =============================================================================

import { useState, useMemo } from "react";
import type { ProfileLearningField } from "@capture/shared";
import { useProfileLearning } from "../hooks/useProfileLearning";
import { useJobTypes } from "../../hooks/useJobTypes";
import { MiniSparkline } from "@/features/queues/components/MiniSparkline";
import { PanelCard, PanelStatus, PanelError } from "./PanelCard";
import styles from "../ocr-health.module.css";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type SortKey = "fieldName" | "calibratedConfidence" | "skipRate";
type SortDir = "asc" | "desc";

interface FlatRow extends ProfileLearningField {
  formName: string | null;
  /** Rows that need attention float to the top. */
  pinned: boolean;
  // trend?: number[] inherited from ProfileLearningField
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatPct(v: number): string {
  return `${Math.round(v * 100)}%`;
}

function formatConf(v: number): string {
  return v.toFixed(2);
}

/** Relative timestamp ("2h ago", "3d ago", etc.) */
function relativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const secs = Math.floor(diffMs / 1000);
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function LifecycleBadge({ lifecycle }: { lifecycle: ProfileLearningField["lifecycle"] }) {
  return (
    <span className={`${styles.lifecycleBadge} ${styles[`lifecycle_${lifecycle}`]}`}>
      {lifecycle}
    </span>
  );
}

function SortableHeader({
  label,
  sortKey,
  currentKey,
  dir,
  onSort,
}: {
  label: string;
  sortKey: SortKey;
  currentKey: SortKey;
  dir: SortDir;
  onSort: (k: SortKey) => void;
}) {
  const active = sortKey === currentKey;
  return (
    <th
      className={`${styles.sortableHeader} ${active ? styles.sortableHeaderActive : ""}`}
      onClick={() => onSort(sortKey)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onSort(sortKey);
      }}
      aria-sort={active ? (dir === "asc" ? "ascending" : "descending") : "none"}
    >
      {label}
      {active && (
        <span className={styles.sortIndicator}>{dir === "asc" ? " ▲" : " ▼"}</span>
      )}
    </th>
  );
}

// ---------------------------------------------------------------------------
// Summary strip
// ---------------------------------------------------------------------------

function SummaryStrip({
  learning,
  proven,
  demoted,
  autoAdvancedPct,
}: {
  learning: number;
  proven: number;
  demoted: number;
  autoAdvancedPct: number;
}) {
  return (
    <div className={styles.learningStrip}>
      <span className={styles.learningStripItem}>
        <span className={`${styles.lifecycleBadge} ${styles.lifecycle_learning}`}>
          learning
        </span>
        <span className={styles.learningStripCount}>{learning}</span>
      </span>
      <span className={styles.learningStripItem}>
        <span className={`${styles.lifecycleBadge} ${styles.lifecycle_proven}`}>
          proven
        </span>
        <span className={styles.learningStripCount}>{proven}</span>
      </span>
      <span className={styles.learningStripItem}>
        <span className={`${styles.lifecycleBadge} ${styles.lifecycle_demoted}`}>
          demoted
        </span>
        <span className={styles.learningStripCount}>{demoted}</span>
      </span>
      <span className={styles.learningStripSep} />
      <span className={styles.learningStripItem}>
        <span className={styles.statLabel}>Auto-advanced</span>
        <span className={styles.learningStripPct}>{formatPct(autoAdvancedPct)}</span>
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main panel
// ---------------------------------------------------------------------------

export interface ProfileFieldLearningPanelProps {
  /** Pre-selected job type id (e.g., when the panel is embedded with context). */
  jobTypeId?: string;
}

export function ProfileFieldLearningPanel({
  jobTypeId: externalJobTypeId,
}: ProfileFieldLearningPanelProps = {}) {
  const [selectedJobTypeId, setSelectedJobTypeId] = useState<string | undefined>(
    externalJobTypeId,
  );
  const [selectedFormId, setSelectedFormId] = useState<string | undefined>(undefined);
  const [sortKey, setSortKey] = useState<SortKey>("fieldName");
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  // Job type list for the selector.
  const { items: jobTypes } = useJobTypes();

  const { forms, available, fetchedAt, summary, isLoading, error } = useProfileLearning(
    selectedJobTypeId,
    selectedFormId,
    /* withTrend */ true,
  );

  // Form options from the returned learning data (avoids a second API call).
  const formOptions = forms.map((f) => ({ id: f.formId, name: f.formName ?? f.formId }));

  // Flatten all fields into a single sorted list.
  const rows: FlatRow[] = useMemo(() => {
    const filtered = selectedFormId
      ? forms.filter((f) => f.formId === selectedFormId)
      : forms;

    const flat: FlatRow[] = filtered.flatMap((form) =>
      form.fields.map((f) => ({
        ...f,
        formName: form.formName,
        pinned: f.lifecycle === "demoted" || f.spatialSpread === "drifting",
      })),
    );

    return flat.sort((a, b) => {
      // Pinned rows always float first.
      if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;

      // Secondary sort by selected key.
      let cmp = 0;
      if (sortKey === "calibratedConfidence") {
        cmp = a.calibratedConfidence - b.calibratedConfidence;
      } else if (sortKey === "skipRate") {
        cmp = a.skipRate - b.skipRate;
      } else {
        cmp = a.fieldName.localeCompare(b.fieldName);
      }
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [forms, selectedFormId, sortKey, sortDir]);

  function handleSort(key: SortKey) {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  }

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------
  return (
    <PanelCard title="Profile Field Learning">
      {/* Filters */}
      <div className={styles.tableFilter}>
        <div className={styles.learningFilters}>
          <select
            className={styles.filterSelect}
            value={selectedJobTypeId ?? ""}
            onChange={(e) => {
              setSelectedJobTypeId(e.target.value || undefined);
              setSelectedFormId(undefined);
            }}
            aria-label="Select job type"
          >
            <option value="">— Select job type —</option>
            {jobTypes.map((jt) => (
              <option key={jt.id} value={jt.id}>
                {jt.name}
              </option>
            ))}
          </select>

          {formOptions.length > 1 && (
            <select
              className={styles.filterSelect}
              value={selectedFormId ?? ""}
              onChange={(e) => setSelectedFormId(e.target.value || undefined)}
              aria-label="Select form"
            >
              <option value="">All forms</option>
              {formOptions.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* States */}
      {!selectedJobTypeId && (
        <PanelStatus>Select a job type to view learning state</PanelStatus>
      )}

      {selectedJobTypeId && isLoading && <PanelStatus>Loading…</PanelStatus>}

      {selectedJobTypeId && !isLoading && error && (
        <PanelError>{error}</PanelError>
      )}

      {/* available:false (and not loading, no error) */}
      {selectedJobTypeId && !isLoading && !error && !available && fetchedAt && (
        <PanelStatus>Learning data unavailable — OCR service unreachable</PanelStatus>
      )}

      {/* available:true, no fields yet */}
      {selectedJobTypeId && !isLoading && !error && available && summary.total === 0 && (
        <PanelStatus>
          No learning data yet — fields appear here as operators key them
        </PanelStatus>
      )}

      {/* Summary strip + table */}
      {selectedJobTypeId && !isLoading && !error && available && summary.total > 0 && (
        <>
          <SummaryStrip
            learning={summary.learning}
            proven={summary.proven}
            demoted={summary.demoted}
            autoAdvancedPct={summary.autoAdvancedPct}
          />

          <table className={styles.table}>
            <thead>
              <tr>
                <SortableHeader
                  label="Field"
                  sortKey="fieldName"
                  currentKey={sortKey}
                  dir={sortDir}
                  onSort={handleSort}
                />
                {formOptions.length > 1 && <th>Form</th>}
                <th>Lifecycle</th>
                <SortableHeader
                  label="Confidence"
                  sortKey="calibratedConfidence"
                  currentKey={sortKey}
                  dir={sortDir}
                  onSort={handleSort}
                />
                <th>Agreement</th>
                <SortableHeader
                  label="Skip rate"
                  sortKey="skipRate"
                  currentKey={sortKey}
                  dir={sortDir}
                  onSort={handleSort}
                />
                <th>Signal</th>
                <th>Spread</th>
                <th>Last seen</th>
                <th>Trend</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr
                  key={`${row.formName ?? ""}-${row.fieldName}-${idx}`}
                  className={row.pinned ? styles.learningRowPinned : undefined}
                >
                  <td>{row.fieldName}</td>
                  {formOptions.length > 1 && (
                    <td className={styles.learningFormName}>
                      {row.formName ?? "—"}
                    </td>
                  )}
                  <td>
                    <LifecycleBadge lifecycle={row.lifecycle} />
                  </td>
                  <td className={styles.numericCell}>
                    {formatConf(row.calibratedConfidence)}
                  </td>
                  <td className={styles.numericCell}>
                    {row.agreementCount}/{row.observationCount}
                  </td>
                  <td className={styles.numericCell}>
                    {formatPct(row.skipRate)}
                  </td>
                  <td>{row.dominantSignal}</td>
                  <td>{row.spatialSpread}</td>
                  <td className={styles.timestampCell}>
                    {relativeTime(row.lastObservedAt)}
                  </td>
                  <td>
                    {row.trend && row.trend.length > 0 ? (
                      <MiniSparkline
                        data={row.trend}
                        ariaLabel={`Confidence trend for ${row.fieldName}`}
                        width={80}
                        height={24}
                      />
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </PanelCard>
  );
}
